import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCRM } from '../context/CRMContext';
import { useProjects } from '../context/ProjectContext';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Contact } from '../types/crm';
import { PlusIcon, SearchIcon, MailIcon, PhoneIcon, BuildingIcon, EyeIcon, PencilIcon, HardHatIcon } from 'lucide-react';
export function LeadFlow() {
  const navigate = useNavigate();
  const {
    selectedCompany
  } = useData();
  const {
    getContactsByCompanyId
  } = useCRM();
  const {
    getProjectsByContactId
  } = useProjects();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<Contact['status'] | 'all'>('all');
  const contacts = selectedCompany ? getContactsByCompanyId(selectedCompany.id) : [];
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.first_name.toLowerCase().includes(searchQuery.toLowerCase()) || contact.last_name.toLowerCase().includes(searchQuery.toLowerCase()) || contact.email_primary.toLowerCase().includes(searchQuery.toLowerCase()) || contact.company.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contact.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const getStatusColor = (status: Contact['status']) => {
    switch (status) {
      case 'lead':
        return 'warning';
      case 'contact':
        return 'info';
      case 'customer':
        return 'success';
      case 'lost':
        return 'default';
      default:
        return 'default';
    }
  };
  const getStatusCounts = () => {
    return {
      all: contacts.length,
      lead: contacts.filter(c => c.status === 'lead').length,
      contact: contacts.filter(c => c.status === 'contact').length,
      customer: contacts.filter(c => c.status === 'customer').length,
      lost: contacts.filter(c => c.status === 'lost').length
    };
  };
  const hasProjects = (contactId: string) => {
    const projects = getProjectsByContactId(contactId);
    return projects.length > 0;
  };
  const handleHardHatClick = (contactId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/projects?contact=${contactId}`);
  };
  const statusCounts = getStatusCounts();
  return <div className="space-y-6" data-id="element-1751">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1752">
        <div data-id="element-1753">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1754">Contacts</h1>
          <p className="text-sm text-gray-600" data-id="element-1755">
            {contacts.length} total contacts
          </p>
        </div>
        <Button onClick={() => navigate('/lead-flow/new')} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1757" />} data-id="element-1756">
          Add Contact
        </Button>
      </div>

      {/* Filters */}
      <Card data-id="element-1758">
        <div className="space-y-4" data-id="element-1759">
          {/* Search */}
          <div className="relative" data-id="element-1760">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1761" />
            <input type="text" placeholder="Search by name, email, or company..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-1762" />
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap gap-2" data-id="element-1763">
            <button onClick={() => setStatusFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1764">
              All ({statusCounts.all})
            </button>
            <button onClick={() => setStatusFilter('lead')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'lead' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1765">
              Leads ({statusCounts.lead})
            </button>
            <button onClick={() => setStatusFilter('contact')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'contact' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1766">
              Contacts ({statusCounts.contact})
            </button>
            <button onClick={() => setStatusFilter('customer')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'customer' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1767">
              Customers ({statusCounts.customer})
            </button>
            <button onClick={() => setStatusFilter('lost')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'lost' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1768">
              Lost ({statusCounts.lost})
            </button>
          </div>
        </div>
      </Card>

      {/* Contacts List */}
      {filteredContacts.length === 0 ? <Card data-id="element-1769">
          <div className="text-center py-12" data-id="element-1770">
            <p className="text-gray-500 mb-4" data-id="element-1771">
              {searchQuery || statusFilter !== 'all' ? 'No contacts match your filters' : 'No contacts yet. Add your first contact!'}
            </p>
            {!searchQuery && statusFilter === 'all' && <Button onClick={() => navigate('/lead-flow/new')} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1773" />} data-id="element-1772">
                Add Contact
              </Button>}
          </div>
        </Card> : <div className="grid grid-cols-1 gap-4" data-id="element-1774">
          {filteredContacts.map(contact => <Card key={contact.id} padding="none" data-id="element-1775">
              <div className="p-6" data-id="element-1776">
                <div className="flex items-start justify-between" data-id="element-1777">
                  <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-1778">
                    {/* Avatar */}
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-lg flex-shrink-0" data-id="element-1779">
                      {contact.first_name[0]}
                      {contact.last_name[0]}
                    </div>

                    {/* Contact Info */}
                    <div className="flex-1 min-w-0" data-id="element-1780">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1781">
                        <h3 className="text-lg font-semibold text-gray-900" data-id="element-1782">
                          {contact.first_name} {contact.last_name}
                        </h3>
                        {hasProjects(contact.id) && <button onClick={e => handleHardHatClick(contact.id, e)} className="p-1 hover:bg-orange-100 rounded transition-colors" title="View projects" data-id="element-1783">
                            <HardHatIcon className="w-5 h-5 text-orange-600" data-id="element-1784" />
                          </button>}
                        <Badge variant={getStatusColor(contact.status)} data-id="element-1785">
                          {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                        </Badge>
                      </div>

                      <div className="space-y-1" data-id="element-1786">
                        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1787">
                          <BuildingIcon className="w-4 h-4 text-gray-400" data-id="element-1788" />
                          <span data-id="element-1789">{contact.company}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1790">
                          <MailIcon className="w-4 h-4 text-gray-400" data-id="element-1791" />
                          <a href={`mailto:${contact.email_primary}`} className="text-blue-600 hover:text-blue-700" data-id="element-1792">
                            {contact.email_primary}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-1793">
                          <PhoneIcon className="w-4 h-4 text-gray-400" data-id="element-1794" />
                          <a href={`tel:${contact.phone_primary}`} className="text-blue-600 hover:text-blue-700" data-id="element-1795">
                            {contact.phone_primary}
                          </a>
                        </div>
                      </div>

                      {contact.companies.length > 0 && <div className="mt-3 flex flex-wrap gap-2" data-id="element-1796">
                          {contact.companies.map((rel, idx) => <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded" data-id="element-1797">
                              {rel.company_name}
                            </span>)}
                        </div>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4" data-id="element-1798">
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/lead-flow/view/${contact.id}`)} leftIcon={<EyeIcon className="w-4 h-4" data-id="element-1800" />} data-id="element-1799">
                      View
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/lead-flow/edit/${contact.id}`)} leftIcon={<PencilIcon className="w-4 h-4" data-id="element-1802" />} data-id="element-1801">
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
            </Card>)}
        </div>}
    </div>;
}